import { Component, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { GlobalService } from 'src/app/api/global.service';
import { ProjectHealthService, ProjectHealthConfig, ProjectHealthLevel, ProjectHealthResult, PROJECT_STATUS_AWAITING_MISSING_INFO } from 'src/app/api/project-health.service';

@Component({
  selector: 'app-project-health-settings',
  templateUrl: './health-settings.component.html',
  styleUrls: ['./health-settings.component.scss'],
})
export class HealthSettingsComponent implements OnInit {

  config: ProjectHealthConfig = null;
  showAdvanced = false;

  // "Impact level" presets for each weight - shown as a plain-language dropdown instead of a raw
  // decimal input, since a firm admin has no intuition for what "0.5" vs "4" actually means. Each
  // list's numeric values are on that weight's own scale (see project-health.service.ts's
  // CalculateHealth for how each is actually used).
  readonly dueDateLevels = [
    { label: 'None', value: 0 }, { label: 'Low', value: 0.25 }, { label: 'Medium', value: 0.5 },
    { label: 'High', value: 1 }, { label: 'Very High', value: 2 },
  ];
  readonly overdueTaskLevels = [
    { label: 'None', value: 0 }, { label: 'Low', value: 1 }, { label: 'Medium', value: 2 },
    { label: 'High', value: 4 }, { label: 'Very High', value: 8 },
  ];
  // Deliberately a much gentler scale than overdueTaskLevels, even though both weight a 0-1 ratio -
  // unassigned_ratio (see CalculateHealth) now counts ALL outstanding tasks regardless of due date,
  // so it's routinely nonzero on perfectly healthy, far-from-due projects (early tasks just haven't
  // been staffed yet) in a way overdue_ratio never is. The old 1/2/4/8 scale (shared with
  // overdueTaskLevels) made even "Low" tank a distant project's score for completely normal partial
  // staffing.
  readonly unassignedTaskLevels = [
    { label: 'None', value: 0 }, { label: 'Low', value: 0.25 }, { label: 'Medium', value: 0.5 },
    { label: 'High', value: 1 }, { label: 'Very High', value: 2 },
  ];
  readonly estimatedTimeLevels = [
    { label: 'None', value: 0 }, { label: 'Low', value: 0.5 }, { label: 'Medium', value: 1 },
    { label: 'High', value: 2 }, { label: 'Very High', value: 4 },
  ];
  // Same scale as overdueTaskLevels - being blocked waiting on the client is a binary condition
  // (either the project is in that status or it isn't), treated as comparably serious to an
  // overdue task rather than the gentler always-partially-true scale unassignedTaskLevels uses.
  readonly awaitingMissingInfoLevels = [
    { label: 'None', value: 0 }, { label: 'Low', value: 1 }, { label: 'Medium', value: 2 },
    { label: 'High', value: 4 }, { label: 'Very High', value: 8 },
  ];

  // Fixed example projects, matching the scenarios the scoring formula was originally designed
  // against, so the firm sees the effect of their tuning immediately instead of guessing from raw
  // weight numbers. daysUntilDue is the project's own due date; taskDueOffsetDays is the one
  // outstanding task's due date (negative = already overdue) - kept separate so the due-date and
  // task-date factors can each be demonstrated on their own.
  private previewScenarios: { label: string; project: any }[] = [
    { label: 'Due in 500 days, plenty of time', project: this.BuildPreviewProject(30, 500, 500, true) },
    { label: 'Due in 2 days', project: this.BuildPreviewProject(5 * 60, 2, 2, true) },
    { label: 'Due tomorrow', project: this.BuildPreviewProject(5 * 60, 1, 1, true) },
    { label: 'Due today', project: this.BuildPreviewProject(3 * 60, 0, 0, true) },
    { label: 'A task is overdue, assigned', project: this.BuildPreviewProject(5 * 60, 5, -3, true) },
    { label: 'A task is overdue, nobody assigned', project: this.BuildPreviewProject(5 * 60, 5, -3, false) },
    { label: 'A task is due today, nobody assigned', project: this.BuildPreviewProject(5 * 60, 5, 0, false) },
    { label: 'A task is due in 10 days, nobody assigned', project: this.BuildPreviewProject(5 * 60, 5, 10, false) },
    { label: 'A task is due in 30 days, nobody assigned', project: this.BuildPreviewProject(5 * 60, 5, 30, false) },
    { label: 'Awaiting missing info from client', project: this.BuildPreviewProject(30, 500, 500, true, true) },
  ];

  constructor(
    public modalController: ModalController,
    public globalService: GlobalService,
    private projectHealthService: ProjectHealthService,
    private alertController: AlertController
  ) { }

  async ngOnInit() {
    const stored = await this.projectHealthService.GetConfig();
    // Deep-clone so edits here don't mutate the live cached config until Save() is actually clicked.
    this.config = JSON.parse(JSON.stringify(stored));
  }

  private BuildPreviewProject(remaining_minutes: number, days_until_due: number, taskDueOffsetDays: number, assigned: boolean, awaitingMissingInfo: boolean = false): any {
    const now = new Date();
    const due = new Date(now.getTime() + days_until_due * 24 * 60 * 60 * 1000);
    const taskDue = new Date(now.getTime() + taskDueOffsetDays * 24 * 60 * 60 * 1000);
    return {
      status: awaitingMissingInfo ? PROJECT_STATUS_AWAITING_MISSING_INFO : 0,
      paused: 0,
      progress: 0,
      estimated_time: remaining_minutes,
      estimated_time_remaining: remaining_minutes,
      internal_start_date: now.toISOString(),
      internal_end_date: due.toISOString(),
      // One outstanding task (TaskStatus.completed is 1, so anything else (0 here) counts as still
      // outstanding), due per taskDueOffsetDays and assigned or not per the scenario.
      tasks: [{ status: 0, due_date: taskDue.toISOString(), assigned_member_guid: assigned ? 'preview-team-member' : '' }],
    };
  }

  get previews(): { label: string; result: ProjectHealthResult }[] {
    if (!this.config) return [];
    return this.previewScenarios.map(s => ({
      label: s.label,
      result: this.projectHealthService.CalculateHealth(s.project, this.config)
    }));
  }

  AddLevel() {
    this.config.levels.push({ min_score: 0, label: 'New Level', colour: '#999999' });
  }

  RemoveLevel(index: number) {
    this.config.levels.splice(index, 1);
  }

  DropLevel(event: CdkDragDrop<ProjectHealthLevel[]>) {
    moveItemInArray(this.config.levels, event.previousIndex, event.currentIndex);
  }

  async ResetToDefaults() {
    const alert = await this.alertController.create({
      header: 'Reset to Default?',
      message: `Are you sure you want to reset the project health settings to their default values? This action cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Reset',
          cssClass: 'alert-button-danger',
          handler: async () => {
            this.config = this.projectHealthService.GetDefaultConfig();
          }
        }
      ]
    });
    await alert.present();

    this.config = this.projectHealthService.GetDefaultConfig();
  }

  async Save() {
    const result = await this.projectHealthService.SaveConfig(this.config);
    this.globalService.presentToastMessage(result.message || (result.success ? 'Project health scoring updated.' : 'Could not save.'), result.success);
    if (result.success) this.modalController.dismiss({ success: true });
  }

  Close() {
    this.modalController.dismiss(null);
  }
}
